'use client';

import classNames from 'classnames';
import { MenuIcon, XIcon } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { useRouteChange } from '@/hooks/useRouteChange';

import DesktopDropdownMenu from './DesktopDropdownMenu/DesktopDropdownMenu';
import type { Dropdown } from './Dropdown/types';
import { NavigationButtons } from './NavigationButtons';

import styles from './Navigation.module.scss';

const MobileDropdownMenu = dynamic(
    () => import('./MobileDropdownMenu/MobileDropdownMenu').then((mod) => mod.MobileDropdownMenu),
    { ssr: false },
);

interface Props {
    hasOffset?: boolean;
    dropdowns: Dropdown[];
}

export function NavigationClient({ hasOffset, dropdowns }: Props) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isMobile = useMedia('(max-width: 991px)', false);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.classList.add('navigation-menu-open');
            document.body.classList.add(styles.menuOpen);
        } else {
            document.body.classList.remove('navigation-menu-open');
            document.body.classList.remove(styles.menuOpen);
        }
    }, [isMobileMenuOpen]);

    useRouteChange(() => setIsMobileMenuOpen(false));

    if (isMobile) {
        return (
            <>
                <button
                    title={isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
                    onClick={() => setIsMobileMenuOpen((open) => !open)}
                    className={styles.mobileMenuButton}
                >
                    {isMobileMenuOpen ? (
                        <XIcon className={styles.icon} />
                    ) : (
                        <MenuIcon className={styles.icon} />
                    )}
                </button>
                <nav
                    aria-label="Main navigation"
                    className={classNames(styles.wrapper, {
                        [styles.open]: isMobileMenuOpen,
                        [styles.offset]: hasOffset,
                    })}
                >
                    <MobileDropdownMenu dropdowns={dropdowns} isOpen={isMobileMenuOpen} />
                </nav>
            </>
        );
    }

    return (
        <nav
            aria-label="Main navigation"
            className={classNames(styles.wrapper, {
                [styles.open]: isMobileMenuOpen,
                [styles.offset]: hasOffset,
            })}
        >
            <DesktopDropdownMenu dropdowns={dropdowns} />
            <NavigationButtons className={styles.navigationButtons} />
        </nav>
    );
}
